<template>
  <div>
    <div class="calculation-headers">
      <div class="performance">
        <h2>COMPANY PERFORMANCE</h2>
        <p style="font-size: 18px;">This graph illustrates company performance and the size of your projected Potential Award. This value may be modified by other factors detailed in the calculation below.</p>
      </div>
    </div>

    <plan-card class="plan-calculation">
      <div class="charts">
        <line-chart
          v-if="hasHistory"
          class="line-chart"
          :dates="selected.plan.dates"
          :tiers="selected.tiers"
          :client="selected.client"
        />
      </div>

      <div v-if="hasSplitFunding">
        <div>
          <div>
            <h2 class="split-fund-breakdown">Calculation Breakdown</h2>
          </div>
          <table class="allocation-pools-table-mobile">
            <tr><td class="header-cell" colspan="2">Company Performance</td></tr>
            <tr class="forecast-row">
              <td>{{ selected.tiers.base.label }}</td>
              <td>{{ formatCurrency(selected.allocation_pools.company.base) }}</td>
            </tr>
            <tr class="forecast-row">
              <td>{{ selected.tiers.target.label }}</td>
              <td>{{ formatCurrency(selected.allocation_pools.company.target) }}</td>
            </tr>
            <tr class="forecast-row">
              <td>{{ selected.tiers.superior.label }}</td>
              <td>{{ formatCurrency(selected.allocation_pools.company.superior) }}</td>
            </tr>
            <tr class="forecast-row">
              <td>{{ selected.forecast.label }}</td>
              <td>{{ formatCurrency(selected.client.allocation.contribution) }}</td>
            </tr>
            <template
              v-for="unit in selected.organization.units"
              :key="`unit-${unit.id}-label`"
            >
              <tr><td class="header-cell" colspan="2">{{ selected.organization.types[unit.type_id].label }} Performance</td></tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.base.label }}</td>
                <td>{{ formatCurrency(selected.allocation_pools.organization_type[unit.type_id].base) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.target.label }}</td>
                <td>{{ formatCurrency(selected.allocation_pools.organization_type[unit.type_id].target) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.superior.label }}</td>
                <td>{{ formatCurrency(selected.allocation_pools.organization_type[unit.type_id].superior) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.forecast.label }}</td>
                <td>{{ formatCurrency(unit.allocation.contribution) }}</td>
              </tr>
            </template>
            <template v-if="hasIndividualComponent">
              <tr><td class="header-cell" colspan="2">Individual Performance</td></tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.base.label }}</td>
                <td>{{ formatCurrency(selected.allocation_pools.individual.base) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.target.label }}</td>
                <td>{{ formatCurrency(selected.allocation_pools.individual.target) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.superior.label }}</td>
                <td>{{ formatCurrency(selected.allocation_pools.individual.superior) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.forecast.label }}</td>
                <td>{{ formatCurrency(selected.employee.allocation.contribution) }}</td>
              </tr>
            </template>
            <template v-if="hasIndividualPerformance">
              <tr><td class="header-cell" colspan="2">Individual Performance</td></tr>
              <tr class="forecast-row">
                <td colspan="2" style="padding: 0; text-align: center;">{{ formatPercent(selected.employee.allocation.modifier) }}</td>
              </tr>
            </template>
            <template v-if="selected.forecast.tweaked_percent !== 0 || selected.forecast.tweaked_cents !== 0">
              <tr><td class="header-cell" colspan="2">Total</td></tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.base.label }}</td>
                <td>{{ formatCurrency(selected.tiers.base.value) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.target.label }}</td>
                <td>{{ formatCurrency(selected.tiers.target.value) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.superior.label }}</td>
                <td>{{ formatCurrency(selected.tiers.superior.value) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.forecast.label }}</td>
                <td>{{ formatCurrency(selected.forecast.modified) }}</td>
              </tr>

              <template v-if="selected.forecast.tweaked_percent !== 0">
                <tr><td class="header-cell" colspan="2">Employer Adjustment</td></tr>
                <tr class="forecast-row"><td colspan="2" class="text-center p-0">{{ selected.forecast.tweaked_percent < 0 ? '-' : '+' }}{{ formatPercent(selected.forecast.tweaked_percent) }}</td></tr>
              </template>

              <template v-if="selected.forecast.tweaked_cents !== 0">
                <tr><td class="header-cell" colspan="2">Employer Adjustment</td></tr>
                <tr class="forecast-row"><td colspan="2" class="text-center p-0">{{ selected.forecast.tweaked_cents < 0 ? '-' : '+' }}{{ formatCurrency(selected.forecast.tweaked_cents) }}</td></tr>
              </template>

              <tr><td class="header-cell" colspan="2">Award Value</td></tr>
              <tr class="forecast-row"><td colspan="2" class="text-center p-0">{{ formatCurrency(selected.forecast.adjusted) }}</td></tr>
            </template>
            <template v-else>
              <tr><td class="header-cell" colspan="2">Award Value</td></tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.base.label }}</td>
                <td>{{ formatCurrency(selected.tiers.base.value) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.target.label }}</td>
                <td>{{ formatCurrency(selected.tiers.target.value) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.tiers.superior.label }}</td>
                <td>{{ formatCurrency(selected.tiers.superior.value) }}</td>
              </tr>
              <tr class="forecast-row">
                <td>{{ selected.forecast.label }}</td>
                <td>{{ formatCurrency(selected.forecast.adjusted) }}</td>
              </tr>
            </template>
          </table>
          <div class="allocation-pools-table">
            <div class="header-cell" />
            <div class="header-cell">
              {{ selected.tiers.base.label }}
            </div>
            <div class="header-cell">
              {{ selected.tiers.target.label }}
            </div>
            <div class="header-cell">
              {{ selected.tiers.superior.label }}
            </div>
            <div class="header-cell last-column">
              {{ selected.forecast.label }}
            </div>

            <div class="first-column">
              Company Performance
            </div>
            <div>{{ formatCurrency(selected.allocation_pools.company.base) }}</div>
            <div>{{ formatCurrency(selected.allocation_pools.company.target) }}</div>
            <div>{{ formatCurrency(selected.allocation_pools.company.superior) }}</div>
            <div class="last-column">
              {{ formatCurrency(selected.client.allocation.contribution) }}
            </div>

            <template
              v-for="unit in selected.organization.units"
              :key="`unit-${unit.id}-label`"
            >
              <div class="first-column">
                {{ selected.organization.types[unit.type_id].label }} Performance
              </div>
              <div>{{ formatCurrency(selected.allocation_pools.organization_type[unit.type_id].base) }}</div>
              <div>{{ formatCurrency(selected.allocation_pools.organization_type[unit.type_id].target) }}</div>
              <div>{{ formatCurrency(selected.allocation_pools.organization_type[unit.type_id].superior) }}</div>
              <div class="last-column">
                {{ formatCurrency(unit.allocation.contribution) }}
              </div>
            </template>

            <template v-if="hasIndividualComponent">
              <div class="first-column">
                Individual Performance
              </div>
              <div>{{ formatCurrency(selected.allocation_pools.individual.base) }}</div>
              <div>{{ formatCurrency(selected.allocation_pools.individual.target) }}</div>
              <div>{{ formatCurrency(selected.allocation_pools.individual.superior) }}</div>
              <div class="last-column">
                {{ formatCurrency(selected.employee.allocation.contribution) }}
              </div>
            </template>

            <template v-if="hasIndividualPerformance">
              <div class="first-column">Individual Performance</div>
              <div />
              <div />
              <div />
              <div class="last-column">
                {{ formatPercent(selected.employee.allocation.modifier) }}
              </div>
            </template>
            <template v-if="selected.forecast.tweaked_percent !== 0 || selected.forecast.tweaked_cents !== 0">
              <div class="first-column border-t" />
              <div class="border-t">
                {{ formatCurrency(selected.tiers.base.value) }}
              </div>
              <div class="border-t">
                {{ formatCurrency(selected.tiers.target.value) }}
              </div>
              <div class="border-t">
                {{ formatCurrency(selected.tiers.superior.value) }}
              </div>
              <div class="last-column border-t">
                {{ formatCurrency(selected.forecast.modified) }}
              </div>

              <template v-if="selected.forecast.tweaked_percent !== 0">
                <div class="first-column">
                  Employer Adjustment
                </div>
                <div />
                <div />
                <div />
                <div class="last-column">
                  {{ selected.forecast.tweaked_percent < 0 ? '-' : '+' }}{{ formatPercent(selected.forecast.tweaked_percent) }}
                </div>
              </template>

              <template v-if="selected.forecast.tweaked_cents !== 0">
                <div class="first-column">
                  Employer Adjustment
                </div>
                <div />
                <div />
                <div />
                <div class="last-column">
                  {{ selected.forecast.tweaked_cents < 0 ? '-' : '+' }}{{ formatCurrency(selected.forecast.tweaked_cents) }}
                </div>
              </template>

              <div class="first-column">
                Award Value
              </div>
              <div />
              <div />
              <div />
              <div class="last-column">
                {{ formatCurrency(selected.forecast.adjusted) }}
              </div>
            </template>
            <template v-else>
              <div class="border-t" />
              <div class="border-t">
                {{ formatCurrency(selected.tiers.base.value) }}
              </div>
              <div class="border-t">
                {{ formatCurrency(selected.tiers.target.value) }}
              </div>
              <div class="border-t">
                {{ formatCurrency(selected.tiers.superior.value) }}
              </div>
              <div class="last-column border-t">
                {{ formatCurrency(selected.forecast.adjusted) }}
              </div>
            </template>
          </div>
        </div>
      </div>
      <plan-distributions
        v-else-if="hasDistributions"
        :distributions="selected.distributions"
        :organization="selected.organization"
        :employee="selected.employee"
        :plan="selected.plan"
        :client="selected.client"
      />
      <plan-breakdown
        v-else
        :distributions="selected.distributions"
        :client="selected.client"
        :employee="selected.employee"
        :organization="selected.organization"
        :forecast="selected.forecast"
      />
    </plan-card>
  </div>
</template>

<script>
import PlanCard from "@/components/plans/global/PlanCard.vue";
import PlanBreakdown from "@/components/plans/short-term/calculation/PlanBreakdown.vue";
import LineChart from "@/components/plans/short-term/charts/LineChart.vue";
import PlanDistributions from "@/components/plans/short-term/PlanDistributions.vue";
import VLcurrency from "@/composables/VLcurrency";
import VLpercent from '@/composables/VLpercent';

export default {
    name: "PlanCalculation",

    components: {
      PlanDistributions,
      LineChart,
      PlanCard,
      PlanBreakdown,
    },

    props: {
      selected: {
        type: Object,
        required: true,
      },
    },

    computed: {
        hasIndividualPerformance() {
          return (this.selected.employee.performance.goals || this.selected.employee.performance.rating) && this.selected.employee.performance.mode === 'as-modifier';
        },
        hasIndividualComponent() {
          return this.selected?.employee?.performance?.mode === 'as-component'
        },
        hasSplitFunding() {
          return this.selected?.plan?.funding_mode === 'split';
        },
        hasHistory() {
          return this.selected.client?.performance
        },
        isEos() {
          return this.selected.plan.isEos;
        },
        hasDistributions() {
          return this.selected.plan?.holdback.mode === 'enabled'
        },
        hasOrganizationUnits() {
          return this.selected.organization?.units && Object.values(this.selected.organization?.units).length > 0
        },
    },

    methods: {
        formatCurrency(value, decimals = 0, currency = 'USD') {
            return VLcurrency.formatCurrency(value, decimals, currency);
        },
        formatPercent(value, decimals = 0) {
            return VLpercent.formatPercent(value, decimals);
        }
    }
}
</script>

<style lang="scss" scoped>
@use "../../../../scss/_variables.scss";
.mobile-allocation-header, .mobile-line-chart {
  display: none;
}
.calculation-headers {
  display: flex;

  margin-bottom: 1.125rem;

  .performance, .allocation {
    p {
      font-size: variables.$text-sm;
    }
  }

  .performance {}

  .allocation {
    min-width: 28%;
    margin-left: 1.25em;

    @media screen and (max-width: 1650px) {
      display: none;
    }
  }
}

.plan-calculation {
  padding: 1.2rem 2.6rem;

  .charts {
    display: flex;

    padding-bottom: 3.6rem;

    border-bottom: 2px solid variables.$cloud;

    .pie-chart {
      @media screen and (min-width: 541px) {
        margin-left: 1.25em;
      }
      max-width: 300px;
    }

    @media screen and (max-width: 540px) {
      margin: -1.2rem -2.4rem 0;
      padding-bottom: 0;
    }

    @media screen and (max-width: 1650px) {
      flex-direction: column;

      .mobile-allocation-header, .mobile-line-chart {
        display: unset;
      }

      .line-chart {
        display: none;
      }
    }
  }
}
.allocation-pools-table-mobile {
  display: none;
  width: 100%;
  .header-cell {
    font-weight: bold;
    font-size: 1rem;
    text-transform: capitalize;
    text-align: center;
  }

  .forecast-row {
    td {
      width: 50%;
    }
    td:first-child {
      font-weight: bold;
      text-align: right;
      padding-right: 12px;
    }
  }

  tr:not(.forecast-row):not(:first-of-type) {
    td {
      padding-top: 12px;
    }
  }
}
.allocation-pools-table {
  width:100%;
  display:grid;
  grid-template-columns:5fr repeat(4, 4fr);
  margin-bottom:0.5em;
}

.allocation-pools-table > div {
  text-align: center;

  padding: 0.5em 0.75em;
}

.allocation-pools-table > .header-cell {
  font-weight: bold;
  font-size: 1rem;

  text-transform: capitalize;

  border-bottom: 1px solid variables.$charcoal-tint-20;
}

.allocation-pools-table > .first-column {
  text-align: right;

  font-weight: bold;
}

.allocation-pools-table > .last-column {
  border-left: 1px solid variables.$charcoal-tint-20;
}

.allocation-pools-table > .border-t {
  border-top: 1px solid variables.$charcoal-tint-20;
}

.text-center {
  text-align: center !important;
}

.p-0 {
  padding: 0 !important;
}

@media screen and (max-width: 750px) {
  .allocation-pools-table {
    display: none;
  }

  .allocation-pools-table-mobile {
    display: table;
  }

  .split-fund-breakdown {
    text-align: center;
  }
}
</style>